import { useState } from 'react';
import { DELETION_REASONS, PENDING_DELETION_WINDOW_DAYS } from '@wineryxplorer/shared';
import type { DeletionReason } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { Button } from '../../components/ui/Button';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';

const DAY_MS = 24 * 60 * 60 * 1000;

const REASON_LABELS: Record<DeletionReason, string> = {
  test_account: 'Test account',
  tos_violation: 'Violated TOS',
  spam: 'Spam',
  other: 'Other',
};

interface PendingDeletionBannerProps {
  userId: string;
  pendingDeletion: { at: string; by: string; reason: string; note?: string | null };
  /** Restore is SUPER_ADMIN-only; other admins see the banner without it. */
  canRestore: boolean;
}

/** Days until the account may be purged, never negative. */
const daysRemaining = (at: string) =>
  Math.max(
    0,
    Math.ceil(
      (new Date(at).getTime() + PENDING_DELETION_WINDOW_DAYS * DAY_MS - Date.now()) / DAY_MS
    )
  );

export const PendingDeletionBanner = ({
  userId,
  pendingDeletion,
  canRestore,
}: PendingDeletionBannerProps) => {
  const utils = trpc.useUtils();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const restoreMutation = trpc.adminUsers.restore.useMutation({
    onSuccess: () => {
      setConfirmOpen(false);
      utils.adminUsers.getById.invalidate({ id: userId });
      utils.adminUsers.list.invalidate();
      utils.adminUsers.pendingDeletions.invalidate();
    },
    // Close the modal so the banner's error is not hidden behind its backdrop.
    onError: () => setConfirmOpen(false),
  });

  const reason = (DELETION_REASONS as readonly string[]).includes(pendingDeletion.reason)
    ? REASON_LABELS[pendingDeletion.reason as DeletionReason]
    : pendingDeletion.reason;

  const days = daysRemaining(pendingDeletion.at);

  return (
    <>
      <div className="mb-6 flex items-center justify-between gap-3 rounded-lg border border-error bg-red-50 p-4 text-sm text-error">
        <div>
          <span>
            {`Pending deletion — ${days} ${days === 1 ? 'day' : 'days'} left (${reason})`}
          </span>
          {restoreMutation.error && (
            <p role="alert" className="mt-1">
              {restoreMutation.error.message}
            </p>
          )}
        </div>
        {canRestore && (
          <Button variant="outline" onClick={() => setConfirmOpen(true)}>
            Restore
          </Button>
        )}
      </div>
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => restoreMutation.mutate({ id: userId })}
        title="Restore Account"
        message="Restore this account? Their content comes back and their email is unbanned."
        confirmText="Restore"
        isLoading={restoreMutation.isPending}
      />
    </>
  );
};
